import { makeAutoObservable } from 'mobx';
import { ApiError, CreateRequesterTokenRequest, PagedRequesterTokens, RequesterToken, RequesterTokensService, UpdateRequesterTokenRequest } from 'services/featuregating/generated';

import { Loadable } from './common';

export type { PagedRequesterTokens, RequesterToken }

export interface IRequesterTokenStore
{
  pagedRequesterTokens: Loadable<PagedRequesterTokens>,
  requesterToken: Loadable<RequesterToken>,

  findAsync: (query?: string, page?: number, pageSize?: number) => Promise<void>,
  getByTokenAsync: (token: string) => Promise<void>,
  createAsync: (requester_id: number, token: string) => Promise<void>,
  deleteAsync: (token: string) => Promise<void>,
  updateAsync: (token: string, requester_id: number) => Promise<void>,
}

export class RequesterTokenStore implements IRequesterTokenStore
{
  pagedRequesterTokens: Loadable<PagedRequesterTokens> = new Loadable<PagedRequesterTokens>();
  requesterToken: Loadable<RequesterToken> = new Loadable<RequesterToken>();

  constructor()
  {
    makeAutoObservable(this);
  }

  async findAsync(query: string = "", page: number = 0, pageSize: number = 30)
  {
    await this.usingLoadingAsync
    (
      this.pagedRequesterTokens,
      async () =>
      {
        const pagedRequesterTokens = await RequesterTokensService.getRequesterTokens({query: query, page: `${page}`, pageSize: `${pageSize}`});
        this.setPagedRequesterTokens(pagedRequesterTokens);
      },
    );
  }

  async getByTokenAsync(token: string)
  {
    await this.usingLoadingAsync
    (
      this.requesterToken,
      async () =>
      {
        const requesterToken = await RequesterTokensService.getRequesterTokenByToken({token: token});
        this.setRequesterToken(requesterToken);
      },
      (error: Error) =>
      {
        const apiError = error as ApiError;
        if (apiError && apiError.status === 404)
        {
          this.setRequesterToken();
        }
        else
        {
          this.requesterToken.setError(error);
        }
      },
    );
  }

  async updateAsync(token: string, requester_id: number)
  {
    const request: UpdateRequesterTokenRequest =
    {
      requester_id: requester_id,
    };
    await this.usingLoadingAsync
    (
      this.requesterToken,
      async () =>
      {
        const requesterToken = await RequesterTokensService.patchRequesterTokenByToken({token: token, requestBody: request});
        this.setRequesterToken(requesterToken);
        await this.pagedRequesterTokens.refreshAsync();
      },
    );
  }

  async createAsync(requester_id: number, token: string)
  {
    const request: CreateRequesterTokenRequest =
    {
      requester_id: requester_id,
      token: token,
    };
    await this.usingLoadingAsync
    (
      this.requesterToken,
      async () =>
      {
        const token = await RequesterTokensService.postRequesterTokens({requestBody: request});
        this.setRequesterToken(token);
        await this.pagedRequesterTokens.refreshAsync();
      },
    );
  }

  async deleteAsync(token: string)
  {
    await this.usingLoadingAsync
    (
      this.requesterToken,
      async () =>
      {
        await RequesterTokensService.deleteRequesterTokenByToken({token: token});
        this.setRequesterToken();
        await this.pagedRequesterTokens.refreshAsync();
      },
    );
  }

  private async usingLoadingAsync<T>(loadable: Loadable<T>, actionAsync: () => Promise<void>, errorHandler?: <T extends Error>(e: T) => void)
  {
    loadable.clearError();
    loadable.setIsLoading(true);
    try
    {
      await actionAsync();
      loadable.dataRefreshOperator = async () => await actionAsync();
    }
    catch (e)
    {
      const error = e as Error;
      if (errorHandler)
      {
        errorHandler(error);
      }
      else
      {
        loadable.setError(error);
      }
    }
    finally
    {
      loadable.setIsLoading(false);
    }
  }
  private setPagedRequesterTokens(pagedRequesterTokens?: PagedRequesterTokens): void
  {
    this.pagedRequesterTokens.data = pagedRequesterTokens;
  }
  private setRequesterToken(requesterToken?: RequesterToken): void
  {
    this.requesterToken.data = requesterToken;
  }
}